import React, { useState } from 'react';
import css from '../calculatorStyles.module.css';
import { calculateAltimeter } from '../engine/altimeterEngine';
import { MANUFACTURERS } from '../config/altimeterConfig';
import ResultDisplay from './ResultDisplay';
import { formatLabel, CONDITION_OPTIONS } from '../calculatorUtils';

const TYPE_OPTIONS = [
  { value: 'digital', label: 'Digital' },
  { value: 'audible', label: 'Audible' },
  { value: 'analogue', label: 'Analogue' },
];

const AltimeterCalculator = () => {
  const [customPrice, setCustomPrice] = useState(false);
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [altiType, setAltiType] = useState('digital');
  const [domYear, setDomYear] = useState('');
  const [condition, setCondition] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const mfrOptions = Object.keys(MANUFACTURERS).sort();
  const modelOptions = manufacturer
    ? Object.keys(MANUFACTURERS[manufacturer]?.models || {}).sort()
    : [];

  const handleManufacturerChange = e => {
    setManufacturer(e.target.value);
    setModel('');
    setResults(null);
  };

  const handleCalculate = () => {
    setError(null);
    try {
      if (!domYear) {
        throw new Error('Please enter a DOM year.');
      }
      const params = {
        domYear: parseInt(domYear, 10),
        condition: condition || undefined,
      };
      if (customPrice) {
        if (!newPrice) {
          throw new Error('Please enter the new price.');
        }
        params.customNewPriceEur = parseFloat(newPrice);
        params.type = altiType;
      } else {
        if (!manufacturer) {
          throw new Error('Please select a manufacturer.');
        }
        if (!model) {
          throw new Error('Please select a model.');
        }
        params.manufacturer = manufacturer;
        params.model = model;
      }
      const result = calculateAltimeter(params);
      setResults(result);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = () => {
    setCustomPrice(false);
    setManufacturer('');
    setModel('');
    setNewPrice('');
    setAltiType('digital');
    setDomYear('');
    setCondition('');
    setResults(null);
    setError(null);
  };

  return (
    <div className={css.calculator}>
      <div className={css.card}>
        <label className={css.checkboxLabel}>
          <input
            type="checkbox"
            checked={customPrice}
            onChange={e => { setCustomPrice(e.target.checked); setResults(null); }}
          />
          My model is not listed
        </label>
        <div className={css.grid}>
          {customPrice ? (
            <>
              <div className={css.field}>
                <label className={css.label}>New Price (EUR)</label>
                <input
                  type="number"
                  className={css.input}
                  value={newPrice}
                  onChange={e => setNewPrice(e.target.value)}
                  placeholder="e.g. 350"
                />
              </div>
              <div className={css.field}>
                <label className={css.label}>Type</label>
                <select
                  className={css.select}
                  value={altiType}
                  onChange={e => setAltiType(e.target.value)}
                >
                  {TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className={css.field}>
                <label className={css.label}>Manufacturer</label>
                <select
                  className={css.select}
                  value={manufacturer}
                  onChange={handleManufacturerChange}
                >
                  <option value="">Select...</option>
                  {mfrOptions.map(m => (
                    <option key={m} value={m}>{formatLabel(m)}</option>
                  ))}
                </select>
              </div>
              <div className={css.field}>
                <label className={css.label}>Model</label>
                <select
                  className={css.select}
                  value={model}
                  onChange={e => { setModel(e.target.value); setResults(null); }}
                  disabled={!manufacturer}
                >
                  <option value="">Select...</option>
                  {modelOptions.map(m => (
                    <option key={m} value={m}>{formatLabel(m)}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div className={css.field}>
            <label className={css.label}>DOM Year</label>
            <input
              type="number"
              className={css.input}
              value={domYear}
              onChange={e => setDomYear(e.target.value)}
              placeholder="e.g. 2020"
            />
          </div>
          <div className={css.field}>
            <label className={css.label}>Condition</label>
            <select
              className={css.select}
              value={condition}
              onChange={e => setCondition(e.target.value)}
            >
              <option value="">Select...</option>
              {CONDITION_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className={css.actions}>
          <button type="button" className={css.btnPrimary} onClick={handleCalculate}>
            Calculate
          </button>
          <button type="button" className={css.btnSecondary} onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>
      {error && (
        <div className={`${css.error} ${css.errorVisible}`} role="alert">
          <strong>Error</strong>
          {error}
        </div>
      )}
      {results && <ResultDisplay {...results} />}
    </div>
  );
};

export default AltimeterCalculator;
